<template>
  <div class="flex flex-col gap-6">

    <!-- Header -->
    <div class="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 class="text-2xl font-bold text-foreground">{{ t('reportsPage.title') }}</h1>
        <p class="text-sm text-muted-foreground mt-1">{{ t('reportsPage.subtitle') }}</p>
      </div>
    </div>

    <!-- Controls -->
    <AppCard class="p-5 flex flex-col gap-5">
      <!-- Report type -->
      <div>
        <div class="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{{ t('reportsPage.reportType') }}</div>
        <div class="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-2">
          <button
            v-for="rt in REPORT_TYPES"
            :key="rt.key"
            type="button"
            :class="[
              'group flex items-start gap-3 rounded-lg border p-3 text-start transition-all',
              type === rt.key ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border bg-white hover:border-primary/40 hover:bg-muted/30'
            ]"
            @click="selectType(rt.key)"
          >
            <span :class="['rounded-md p-2 shrink-0', type === rt.key ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground group-hover:text-foreground']">
              <component :is="rt.icon" class="h-4 w-4" />
            </span>
            <span class="min-w-0">
              <span class="block text-sm font-semibold text-foreground">{{ t(`reportsPage.types.${rt.key}`) }}</span>
              <span class="block text-[11px] leading-snug text-muted-foreground mt-0.5">{{ t(`reportsPage.typeDesc.${rt.key}`) }}</span>
            </span>
          </button>
        </div>
      </div>

      <!-- Filters -->
      <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-12 gap-4 items-end">
        <!-- Scope -->
        <div class="xl:col-span-3">
          <label class="block text-xs font-medium text-muted-foreground mb-1.5">{{ t('reportsPage.scope') }}</label>
          <div class="inline-flex w-full rounded-md border border-input bg-muted/40 p-0.5">
            <button
              v-for="sc in ['individual', 'general']"
              :key="sc"
              type="button"
              :class="['flex-1 flex items-center justify-center gap-1.5 rounded px-2 h-8 text-sm font-medium whitespace-nowrap transition-colors', scope === sc ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground']"
              @click="scope = sc"
            >
              <User v-if="sc === 'individual'" class="h-3.5 w-3.5" />
              <Users v-else class="h-3.5 w-3.5" />
              {{ t(`reportsPage.scopes.${sc}`) }}
            </button>
          </div>
        </div>

        <!-- Employee -->
        <div v-if="scope === 'individual'" class="xl:col-span-3">
          <label class="block text-xs font-medium text-muted-foreground mb-1.5">{{ t('reportsPage.employee') }}</label>
          <AppSelect v-model="employeeId" :class="!employeeId ? 'border-amber-300' : ''">
            <option value="">{{ t('reportsPage.selectEmployee') }}</option>
            <option v-for="emp in sortedEmployees" :key="emp.id" :value="String(emp.id)">{{ emp.name }} ({{ emp.code }})</option>
          </AppSelect>
        </div>

        <!-- Period -->
        <div :class="scope === 'individual' ? 'xl:col-span-2' : 'xl:col-span-3'">
          <label class="block text-xs font-medium text-muted-foreground mb-1.5">{{ t('reportsPage.period') }}</label>
          <AppSelect v-model="period">
            <option v-for="p in PERIODS" :key="p" :value="p">{{ t(`reportsPage.periods.${p}`) }}</option>
          </AppSelect>
        </div>

        <div v-if="period === 'custom'" class="xl:col-span-2 grid grid-cols-2 gap-2">
          <div>
            <label class="block text-xs font-medium text-muted-foreground mb-1.5">{{ t('reportsPage.from') }}</label>
            <input v-model="customStart" type="date" class="h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
          <div>
            <label class="block text-xs font-medium text-muted-foreground mb-1.5">{{ t('reportsPage.to') }}</label>
            <input v-model="customEnd" type="date" class="h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
        </div>

        <!-- Rate month (overtime) -->
        <div v-if="type === 'overtime'" class="xl:col-span-2">
          <label class="block text-xs font-medium text-muted-foreground mb-1.5" :title="t('reportsExt.rateBasisHint')">{{ t('reportsExt.rateBasis') }}</label>
          <input v-model="rateMonth" type="month" :title="t('reportsExt.rateBasisHint')" class="h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
        </div>

        <!-- Generate -->
        <div class="xl:col-span-2 xl:ms-auto">
          <AppButton class="w-full" :disabled="!canGenerate || loading" @click="generate">
            <Loader2 v-if="loading" class="h-4 w-4 animate-spin" />
            <FileText v-else class="h-4 w-4" />
            {{ loading ? t('reportsExt.generating') : t('reportsExt.generateReport') }}
          </AppButton>
        </div>
      </div>

      <div class="text-xs text-muted-foreground -mt-1">
        <CalendarRange class="inline h-3.5 w-3.5 me-1 -mt-0.5" />
        <span dir="ltr">{{ range.startDate || '…' }} → {{ range.endDate || t('reportsPage.today') }}</span>
      </div>
    </AppCard>

    <!-- Error -->
    <div v-if="error" class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 flex items-center gap-2">
      <AlertCircle class="h-4 w-4 shrink-0" /> {{ error }}
    </div>

    <!-- Loading -->
    <div v-if="loading && !model" class="flex flex-col gap-4">
      <AppSkeleton class="h-14 w-full" />
      <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <AppSkeleton v-for="i in 4" :key="i" class="h-24" />
      </div>
      <AppSkeleton class="h-72 w-full" />
    </div>

    <!-- Report -->
    <AppCard v-if="model" :class="['p-5 sm:p-6 transition-opacity', loading ? 'opacity-50 pointer-events-none' : '']">
      <div class="flex flex-wrap items-start justify-between gap-4 mb-5 pb-4 border-b border-border" :dir="model.dir">
        <div>
          <h2 class="text-xl font-bold text-foreground">{{ model.title }}</h2>
          <div class="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span v-for="(part, i) in model.subtitle" :key="i">{{ part }}</span>
            <span v-for="(part, i) in model.internalSubtitle || []" :key="`i${i}`" :title="t('reportsExt.screenOnly')">{{ part }} <span class="italic opacity-70">({{ t('reportsExt.screenOnly') }})</span></span>
          </div>
        </div>
        <div class="flex items-center gap-2" dir="ltr">
          <AppButton size="sm" :disabled="exporting === 'pdf'" @click="exportPdf">
            <Loader2 v-if="exporting === 'pdf'" class="h-3.5 w-3.5 animate-spin" />
            <FileDown v-else class="h-3.5 w-3.5" /> PDF
          </AppButton>
          <AppButton size="sm" variant="outline" @click="exportExcel">
            <FileSpreadsheet class="h-3.5 w-3.5" /> Excel
          </AppButton>
          <AppButton size="sm" variant="outline" @click="exportCsv">
            <FileText class="h-3.5 w-3.5" /> CSV
          </AppButton>
        </div>
      </div>
      <ReportView :model="model" />
    </AppCard>

    <!-- Empty state -->
    <AppCard v-if="!model && !loading" class="p-10 text-center">
      <div class="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
        <BarChart3 class="h-6 w-6 text-primary" />
      </div>
      <h3 class="text-base font-semibold text-foreground">{{ t('reportsPage.emptyTitle') }}</h3>
      <p class="mt-1 text-sm text-muted-foreground max-w-lg mx-auto">{{ t('reportsPage.emptyBody') }}</p>
    </AppCard>

    <!-- Toast -->
    <transition name="fade">
      <div v-if="toast" :class="['fixed bottom-6 end-6 z-50 rounded-lg px-4 py-3 text-sm shadow-lg flex items-center gap-2', toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-emerald-600 text-white']">
        <CheckCircle2 v-if="toast.type !== 'error'" class="h-4 w-4" />
        <AlertCircle v-else class="h-4 w-4" />
        {{ toast.text }}
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  Clock, UserCheck, CalendarDays, Timer, AlarmClock, Wallet, User, Users, Loader2, FileText,
  FileDown, FileSpreadsheet, AlertCircle, CheckCircle2, BarChart3, CalendarRange,
} from 'lucide-vue-next'
import { AppCard, AppButton, AppSelect, AppSkeleton } from '@/components/ui'
import ReportView from '@/components/reports/ReportView.vue'
import enhancedReportsService from '@/services/enhancedReportsService'
import adminService from '@/services/adminService'
import { exportModelToExcel, exportModelToCSV } from '@/utils/reportModelExport'

const { t, locale } = useI18n()

const REPORT_TYPES = [
  { key: 'overtime', icon: Clock },
  { key: 'attendance', icon: UserCheck },
  { key: 'leave', icon: CalendarDays },
  { key: 'short-leave', icon: Timer },
  { key: 'late-arrivals', icon: AlarmClock },
  { key: 'payroll', icon: Wallet },
]
const PERIODS = ['thisMonth', 'lastMonth', 'thisQuarter', 'thisYear', 'lastYear', 'allTime', 'custom']

const type = ref('overtime')
const scope = ref('individual')
const employeeId = ref('')
const period = ref('thisYear')
const customStart = ref('')
const customEnd = ref('')
const rateMonth = ref('')

const employees = ref([])
const model = ref(null)
const loading = ref(false)
const exporting = ref(null)
const error = ref('')
const toast = ref(null)

const sortedEmployees = computed(() => [...employees.value].sort((a, b) => a.name.localeCompare(b.name)))
const canGenerate = computed(() => (scope.value === 'general' || !!employeeId.value) && (period.value !== 'custom' || (customStart.value && customEnd.value)))

const pad = n => String(n).padStart(2, '0')
const ymd = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

const range = computed(() => {
  const now = new Date()
  const y = now.getFullYear()
  const m = now.getMonth()
  switch (period.value) {
    case 'thisMonth': return { startDate: ymd(new Date(y, m, 1)), endDate: ymd(now) }
    case 'lastMonth': return { startDate: ymd(new Date(y, m - 1, 1)), endDate: ymd(new Date(y, m, 0)) }
    case 'thisQuarter': return { startDate: ymd(new Date(y, Math.floor(m / 3) * 3, 1)), endDate: ymd(now) }
    case 'thisYear': return { startDate: `${y}-01-01`, endDate: ymd(now) }
    case 'lastYear': return { startDate: `${y - 1}-01-01`, endDate: `${y - 1}-12-31` }
    case 'allTime': return { startDate: '', endDate: '' }
    default: return { startDate: customStart.value, endDate: customEnd.value }
  }
})

const params = () => ({
  employeeId: scope.value === 'individual' ? employeeId.value : null,
  startDate: range.value.startDate,
  endDate: range.value.endDate,
  lang: locale.value === 'ar' ? 'ar' : 'en',
  rateMonth: type.value === 'overtime' ? rateMonth.value : null,
})

function showToast(text, kind = 'success') {
  toast.value = { text, type: kind }
  setTimeout(() => { toast.value = null }, 3500)
}

function errorMessage(e) {
  return e?.response?.data?.message || e?.message || t('reportsPage.failed')
}

async function generate() {
  if (!canGenerate.value) return
  loading.value = true
  error.value = ''
  try {
    model.value = await enhancedReportsService.getReportView(type.value, scope.value, params())
  } catch (e) {
    error.value = errorMessage(e)
    model.value = null
  } finally {
    loading.value = false
  }
}

function selectType(key) {
  type.value = key
}

async function exportPdf() {
  exporting.value = 'pdf'
  try {
    await enhancedReportsService.downloadReportPdf(type.value, scope.value, params())
    showToast(t('reportsExt.pdfExported'))
  } catch (e) {
    showToast(errorMessage(e), 'error')
  } finally {
    exporting.value = null
  }
}

function exportExcel() {
  try { exportModelToExcel(model.value) } catch (e) { showToast(errorMessage(e), 'error') }
}

function exportCsv() {
  try { exportModelToCSV(model.value) } catch (e) { showToast(errorMessage(e), 'error') }
}

// The report on screen always matches the current selection: clear it when the selection changes,
// and regenerate on language change so labels follow the UI.
watch([type, scope, employeeId, period, customStart, customEnd, rateMonth], () => {
  model.value = null
  error.value = ''
})
watch(locale, () => { if (model.value) generate() })

onMounted(async () => {
  try {
    const response = await adminService.getAllEmployees({ limit: 1000 })
    if (response.success && response.employees) {
      employees.value = response.employees.map(emp => ({ id: emp.id, code: emp.employee_id || emp.id, name: emp.name }))
    }
  } catch (e) {
    error.value = errorMessage(e)
  }
})
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity .2s ease, transform .2s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; transform: translateY(6px); }
</style>
